// src/components/reader/LessonCard.jsx
//
// The card shell every idea in a lesson sits in (lesson-3a-layout), plus the
// "Read more" collapse for long bodies. Direction A tokens only -- no ornaments,
// no borders drawn in hex.
//
// One shell, used by the technique/analysis/vocabulary/guide parts, the steps,
// the worked examples, the vocab entries, the annotated sample, the set-text
// blocks and the Lit* sections, so a lesson reads as one system rather than as
// eight renderers that each invented a box.
import React, { useState } from 'react';
import { shouldCollapseBody } from './lessonMeta.js';

// `as` lets a card be a <section>/<li> where that is the right element; the default
// div is correct for the common case.
export default function LessonCard({ children, className = '', as: Tag = 'div', ...rest }) {
    return (
        <Tag
            className={'rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4 ' + className}
            {...rest}>
            {children}
        </Tag>
    );
}

// A card's small heading -- the eyebrow above its prose.
export function LessonCardHeading({ children }) {
    if (!children) return null;
    return <div className="font-display text-book-accent text-xs mb-2 font-semibold">{children}</div>;
}

// Long prose, collapsed to ~6 lines behind a fade with a "Read more" toggle.
//
// The decision to collapse is made from the TEXT (shouldCollapseBody), not from a
// measured DOM height: it has to be stable across the three reading scales and both
// breakpoints, and a body that collapses at one font size but not another reads as a
// bug. A short body is never collapsed -- no toggle, no fade, no wrapper behaviour.
//
// `text` is the raw string the decision is made from; `children` is what actually
// renders (usually the same text wrapped in <AnnotatedText>, which must keep its
// glossary highlighting).
export function ReadMore({ text, children, expandLabel = 'Read more', collapseLabel = 'Show less' }) {
    const collapsible = shouldCollapseBody(text);
    const [open, setOpen] = useState(false);
    if (!collapsible) return <>{children}</>;
    return (
        <div>
            <div
                className={open ? undefined : 'btl-readmore-clip'}
                // The fade is a mask on the text itself rather than an overlay
                // rectangle, so it works on any card background in any theme.
                aria-hidden={false}>
                {children}
            </div>
            <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
                className="mt-2 font-display text-book-accent text-xs font-semibold hover:underline">
                {open ? collapseLabel : expandLabel}
            </button>
        </div>
    );
}
